<script>
	import { onMount, onDestroy } from 'svelte';
	import Hls from 'hls.js';
	import Chip from './Chip.svelte';

	export let cam;
	export let onClose = () => {};

	let mediaEl;
	let state = 'loading';
	let lastGood = null;
	let attempt = 0;
	let refreshTimer = null;
	let retryTimer = null;
	let hls = null;

	const BACKOFF_S = [5, 10, 20, 40];

	function bust(u) {
		return u + (u.includes('?') ? '&' : '?') + '_=' + Date.now();
	}

	function markLive() {
		state = 'live';
		lastGood = new Date();
		attempt = 0;
	}

	function fail() {
		if (hls) { hls.destroy(); hls = null; }
		clearInterval(refreshTimer);
		if (attempt < BACKOFF_S.length) {
			state = 'retrying';
			const wait = BACKOFF_S[attempt++];
			retryTimer = setTimeout(start, wait * 1000);
		} else {
			state = 'dead';
		}
	}

	function startImage() {
		state = 'loading';
		if (!mediaEl) return;
		mediaEl.src = bust(cam.url);
	}

	function onImgLoad() {
		markLive();
		if (cam.refresh) {
			clearInterval(refreshTimer);
			refreshTimer = setInterval(() => {
				if (mediaEl && state === 'live') mediaEl.src = bust(cam.url);
			}, cam.refresh * 1000);
		}
	}

	function startHls() {
		state = 'loading';
		if (!mediaEl) return;
		if (hls) { hls.destroy(); hls = null; }
		if (mediaEl.canPlayType('application/vnd.apple.mpegurl')) {
			mediaEl.src = cam.url;
		} else if (Hls.isSupported()) {
			hls = new Hls({
				manifestLoadingTimeOut: 10000,
				manifestLoadingMaxRetry: 1,
				levelLoadingMaxRetry: 1,
				fragLoadingMaxRetry: 2
			});
			hls.loadSource(cam.url);
			hls.attachMedia(mediaEl);
			hls.on(Hls.Events.MANIFEST_PARSED, () => {
				markLive();
				mediaEl.play().catch(() => {});
			});
			hls.on(Hls.Events.ERROR, (_, data) => {
				if (data.fatal) fail();
			});
		} else {
			fail();
		}
	}

	function start() {
		clearTimeout(retryTimer);
		if (cam.type === 'image') startImage();
		else if (cam.type === 'hls') startHls();
		else if (cam.type === 'iframe') {
			state = 'live';
			lastGood = new Date();
		}
	}

	function manualRetry() {
		attempt = 0;
		start();
	}

	function fmt(ts) {
		if (!ts) return '--:--:--';
		return ts.toTimeString().slice(0, 8);
	}

	onMount(start);

	onDestroy(() => {
		clearInterval(refreshTimer);
		clearTimeout(retryTimer);
		if (hls) hls.destroy();
	});
</script>

<div class="card" class:dead={state === 'dead'}>
	<div class="card-head">
		<span class="cam-name" title={cam.name}>{cam.name}</span>
		<button class="close-btn" title="Close feed" on:click={() => onClose(cam)}>✕</button>
	</div>

	<div class="card-body">
		{#if cam.type === 'image'}
			<img
				bind:this={mediaEl}
				alt={cam.name}
				loading="lazy"
				on:load={onImgLoad}
				on:error={fail}
			/>
		{:else if cam.type === 'hls'}
			<video
				bind:this={mediaEl}
				muted
				autoplay
				playsinline
				controls
			/>
		{:else if cam.type === 'iframe'}
			<iframe
				src={cam.url}
				title={cam.name}
				sandbox="allow-scripts allow-same-origin allow-presentation"
				allow="fullscreen"
				referrerpolicy="no-referrer-when-downgrade"
			></iframe>
		{/if}

		{#if state === 'loading'}
			<div class="overlay">
				<Chip size={48} />
			</div>
		{/if}

		{#if state === 'retrying' || state === 'dead'}
			<div class="overlay">
				<div class="overlay-msg">
					<span class="state-txt">{state === 'dead' ? 'FEED OFFLINE' : `RETRYING (${BACKOFF_S[attempt - 1]}s)`}</span>
					<span class="overlay-sub">last good {fmt(lastGood)}</span>
					{#if state === 'dead'}
						<button class="retry-btn" on:click={manualRetry}>RETRY</button>
					{/if}
				</div>
			</div>
		{/if}
	</div>

	<div class="card-foot">
		<span>{cam.attribution}{cam.hd ? ' · HD' : ''}</span>
		<span class="foot-right">
			{#if cam.target}<em>{cam.target}</em> · {/if}{fmt(lastGood)}
		</span>
	</div>

	<span class="state-dot {state}" title={state}></span>
</div>

<style>
	.card {
		position: relative;
		display: flex;
		flex-direction: column;
		background: #0d131c;
		border: 1px solid #1e2a38;
		border-radius: 3px;
		overflow: hidden;
		min-width: 0;
	}

	.card.dead {
		border-color: #5c1a1a;
	}

	.card-head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		padding: 4px 8px;
		background: #131b26;
		border-bottom: 1px solid #1e2a38;
	}

	.cam-name {
		color: #FDB515;
		font-size: 11px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		letter-spacing: 0.02em;
	}

	.close-btn {
		flex: none;
		background: transparent;
		border: none;
		color: #667b90;
		cursor: pointer;
		font-size: 11px;
		padding: 0 2px;
	}

	.close-btn:hover {
		color: #e63946;
	}

	.card-body {
		position: relative;
		aspect-ratio: 16 / 9;
		background: #070B0F;
	}

	.card-body img,
	.card-body video,
	.card-body iframe {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		border: 0;
		background: #070B0F;
	}

	.overlay {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(5, 5, 5, 0.88);
		z-index: 2;
	}

	.overlay-msg {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;
	}

	.state-txt {
		color: #e63946;
		font-size: 11px;
		letter-spacing: 0.12em;
	}

	.overlay-sub {
		color: #667b90;
		font-size: 10px;
	}

	.retry-btn {
		margin-top: 6px;
		background: transparent;
		border: 1px solid #FDB515;
		color: #FDB515;
		font-family: inherit;
		font-size: 10px;
		letter-spacing: 0.12em;
		padding: 3px 14px;
		cursor: pointer;
	}

	.retry-btn:hover {
		background: #FDB515;
		color: #070B0F;
	}

	.card-foot {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		padding: 3px 8px;
		color: #667b90;
		font-size: 10px;
		border-top: 1px solid #101822;
	}

	.foot-right em {
		color: #9a7815;
		font-style: normal;
		text-transform: uppercase;
		letter-spacing: 0.08em;
	}

	.state-dot {
		position: absolute;
		top: 7px;
		left: 8px;
		width: 6px;
		height: 6px;
		border-radius: 50%;
		z-index: 3;
	}

	/* keep dot clear of the name text */
	.card-head .cam-name {
		padding-left: 10px;
	}

	.state-dot.loading { background: #FDB515; animation: pulse 1.2s infinite; }
	.state-dot.live { background: #22c55e; }
	.state-dot.retrying { background: #f97316; animation: pulse 1.2s infinite; }
	.state-dot.dead { background: #e63946; }

	@keyframes pulse {
		0%, 100% { opacity: 1; }
		50% { opacity: 0.25; }
	}
</style>
